import { documentPageMetadataUserEditStatusSchema } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  type MetadataEvalSampledPage,
  MetadataEvalSampledPageSchema,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalSampling'
import type { EvalPageTrail } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalTrail'
import { z } from 'zod'

const EvalPageTrailDecisionSchema = z.union([
  z.literal('unlabeled'),
  documentPageMetadataUserEditStatusSchema,
])

const EvalPageTrailItemSchema = MetadataEvalSampledPageSchema.extend({
  decision: EvalPageTrailDecisionSchema,
})

const EvalPageTrailSchema = z.object({
  visited: z.array(EvalPageTrailItemSchema),
  cursor: z.number().int().nonnegative(),
})

const EvalSessionStateSchema = z.object({
  trail: EvalPageTrailSchema,
  queuedPages: z.array(MetadataEvalSampledPageSchema),
  snapshotComplete: z.boolean().optional(),
})

export type EvalSessionPersistedState = z.infer<typeof EvalSessionStateSchema>

/** What callers actually hold: the live (readonly) trail/queue shapes, not
 * the zod-inferred (mutable-array) parse result. */
export type EvalSessionStateToPersist = {
  trail: EvalPageTrail
  queuedPages: readonly MetadataEvalSampledPage[]
  snapshotComplete?: boolean
}

const STORAGE_PREFIX = 'data-labeling-eval-queue:'

/** Tab-local sampled queue. Confirm and defer already persist as Rolodex
 * user-edits; this only survives a workspace-tab remount so the session
 * does not resample a new first page. It is not a durable record. */
export function evalSessionStorageKey(scopeKey: string): string {
  return `${STORAGE_PREFIX}${scopeKey}`
}

function sessionStorageOrUndefined(): Storage | undefined {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

export function loadEvalSessionState(
  scopeKey: string
): EvalSessionPersistedState | undefined {
  const storage = sessionStorageOrUndefined()
  if (!storage) return undefined
  const raw = storage.getItem(evalSessionStorageKey(scopeKey))
  if (!raw) return undefined
  try {
    const parsed = EvalSessionStateSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : undefined
  } catch {
    return undefined
  }
}

export function saveEvalSessionState(
  scopeKey: string,
  state: EvalSessionStateToPersist
): void {
  const storage = sessionStorageOrUndefined()
  if (!storage) return
  const key = evalSessionStorageKey(scopeKey)
  if (
    !state.snapshotComplete &&
    state.trail.visited.length === 0 &&
    state.queuedPages.length === 0
  ) {
    storage.removeItem(key)
    return
  }
  try {
    storage.setItem(key, JSON.stringify(state))
  } catch {
    // Storage may be full or unavailable (private browsing); best effort only.
  }
}
